import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, Pencil, Trash2, Users as UsersIcon, Mail, Search, Shield,
  ShieldCheck, ClipboardCheck, Truck, ChefHat, User, Ban, ShieldOff, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const ALL_ROLES = [
  { value: "cliente",    label: "Cliente",    Icon: User,           tip: "Revendedor. Hace pedidos desde el catálogo." },
  { value: "encargado",  label: "Encargado",  Icon: ClipboardCheck, tip: "Opera todo menos precios, usuarios y auditoría." },
  { value: "repartidor", label: "Repartidor", Icon: Truck,          tip: "Solo despacho: marca pedidos como entregados." },
  { value: "elaborador", label: "Elaborador", Icon: ChefHat,        tip: "Solo cocina: registra producción y stock." },
  { value: "admin",      label: "Admin",      Icon: ShieldCheck,    tip: "Acceso total. Único que puede asignar roles." },
];

const ROLE_BADGE = {
  admin:      "bg-[#F4D6D6] text-[#8E2E2E] border-[#E8B5B5]",
  encargado:  "bg-[#FDF3E1] text-[#A6751F] border-[#F0DEB2]",
  repartidor: "bg-[#DAE6D7] text-[#3F6B3C] border-[#BCD3B7]",
  elaborador: "bg-[#E6DDF4] text-[#5C3F8C] border-[#C9BBE8]",
  cliente:    "bg-[#E0EAF5] text-[#365E91] border-[#B8C9DD]",
};

const EMPTY = { email: "", password: "", nombre: "", telefono: "", role: "cliente" };

export default function Clientes() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [roleFilter, setRoleFilter] = useState("__all__");
  const [statusFilter, setStatusFilter] = useState("__all__"); // "__all__" | "ACTIVE" | "SUSPENDED"
  const [q, setQ] = useState("");
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendReason, setSuspendReason] = useState("");
  const [suspendBusy, setSuspendBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/users");
      setRows(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = rows;
    if (roleFilter !== "__all__") list = list.filter((u) => u.role === roleFilter);
    if (statusFilter !== "__all__") {
      list = list.filter((u) => (u.account_status || "ACTIVE") === statusFilter);
    }
    if (q.trim()) {
      const qq = q.trim().toLowerCase();
      list = list.filter((u) =>
        (u.nombre || "").toLowerCase().includes(qq) ||
        (u.email || "").toLowerCase().includes(qq) ||
        (u.telefono || "").toLowerCase().includes(qq),
      );
    }
    return list;
  }, [rows, roleFilter, statusFilter, q]);

  const counts = useMemo(() => {
    const c = { total: rows.length };
    for (const r of ALL_ROLES) c[r.value] = rows.filter((u) => u.role === r.value).length;
    c.suspended = rows.filter((u) => (u.account_status || "ACTIVE") === "SUSPENDED").length;
    return c;
  }, [rows]);

  const defaultRoleForNew = roleFilter !== "__all__" ? roleFilter : "cliente";

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, role: defaultRoleForNew });
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      password: "",
      nombre: u.nombre,
      telefono: u.telefono || "",
      role: u.role,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre es obligatorio."); return; }
    if (!editing) {
      if (!form.email.trim()) { toast.error("El email es obligatorio."); return; }
      if (!form.password || form.password.length < 8) {
        toast.error("La contraseña debe tener al menos 8 caracteres.");
        return;
      }
    } else if (form.password && form.password.length < 8) {
      toast.error("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    try {
      if (editing) {
        const payload = {
          nombre: form.nombre.trim(),
          telefono: form.telefono,
          role: form.role,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Usuario actualizado.");
      } else {
        await api.post("/users", {
          email: form.email.trim(),
          password: form.password,
          nombre: form.nombre.trim(),
          telefono: form.telefono,
          role: form.role,
        });
        toast.success("Usuario creado.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar.");
    }
  };

  const toggleActivo = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { activo: !u.activo });
      load();
    } catch { toast.error("Error al cambiar estado."); }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar.");
    }
  };

  const openSuspend = (u) => {
    setSuspendTarget(u);
    setSuspendReason("");
  };

  const confirmSuspend = async () => {
    if (!suspendTarget) return;
    setSuspendBusy(true);
    try {
      await api.post(`/users/${suspendTarget.id}/suspend`, {
        reason: suspendReason.trim() || null,
      });
      toast.success(`Cuenta de ${suspendTarget.nombre} suspendida.`);
      setSuspendTarget(null);
      setSuspendReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al suspender.");
    } finally {
      setSuspendBusy(false);
    }
  };

  const reactivate = async (u) => {
    try {
      await api.post(`/users/${u.id}/reactivate`);
      toast.success(`Cuenta de ${u.nombre} rehabilitada.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al rehabilitar.");
    }
  };

  const editingIsSelf = editing?.id === me?.id;
  const headerSubtitle =
    roleFilter === "__all__"
      ? `Cuentas con acceso a la app. ${counts.total} en total.`
      : `${counts[roleFilter] || 0} ${ALL_ROLES.find((r) => r.value === roleFilter)?.label.toLowerCase()}s.`;

  return (
    <div>
      <PageHeader
        eyebrow="Usuarios"
        title="Clientes y trabajadores"
        subtitle={headerSubtitle}
      >
        {isAdmin && (
          <Button
            className="bg-[#C85A32] hover:bg-[#A64A29] text-white gap-2"
            onClick={openNew}
            data-testid="cli-nuevo"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Nuevo usuario</span>
            <span className="sm:hidden">Nuevo</span>
          </Button>
        )}
      </PageHeader>

      {/* Role chips */}
      <div className="flex flex-wrap gap-2 mb-3">
        <ChipFilter
          active={roleFilter === "__all__"}
          onClick={() => setRoleFilter("__all__")}
          label={`Todos (${counts.total})`}
          testid="cli-filter-all"
        />
        {ALL_ROLES.map(({ value, label, Icon }) => (
          <ChipFilter
            key={value}
            active={roleFilter === value}
            onClick={() => setRoleFilter(value)}
            label={`${label} (${counts[value] || 0})`}
            Icon={Icon}
            testid={`cli-filter-${value}`}
          />
        ))}
      </div>

      {/* Status chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <ChipFilter
          active={statusFilter === "__all__"}
          onClick={() => setStatusFilter("__all__")}
          label="Todos los estados"
          testid="cli-status-all"
        />
        <ChipFilter
          active={statusFilter === "ACTIVE"}
          onClick={() => setStatusFilter("ACTIVE")}
          label={`Activas (${counts.total - counts.suspended})`}
          Icon={ShieldCheck}
          testid="cli-status-active"
        />
        <ChipFilter
          active={statusFilter === "SUSPENDED"}
          onClick={() => setStatusFilter("SUSPENDED")}
          label={`Suspendidas (${counts.suspended})`}
          Icon={ShieldAlert}
          testid="cli-status-suspended"
        />
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C8C8C]" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre, email o teléfono..."
          className="pl-9 h-10 bg-white"
          data-testid="cli-search"
        />
      </div>

      <Card className="border-[#E5E0D8] rounded-2xl bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-[#F9F6F0] border-b border-[#E5E0D8]">
              <tr className="text-[11px] uppercase tracking-[0.14em] text-[#8C8C8C]">
                <th className="px-5 py-3">Nombre</th>
                <th className="px-5 py-3">Rol</th>
                <th className="px-5 py-3">Estado</th>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3 hidden md:table-cell">Teléfono</th>
                <th className="px-5 py-3">Activo</th>
                <th className="px-5 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center text-[#8C8C8C] py-12">Cargando...</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-16">
                  <UsersIcon className="h-10 w-10 text-[#C85A32] mx-auto mb-3 opacity-50" />
                  <div className="font-display font-semibold text-lg">Sin usuarios</div>
                  <p className="text-sm text-[#595959] mt-1 mb-4">
                    {q || roleFilter !== "__all__" || statusFilter !== "__all__"
                      ? "Probá cambiar el filtro o el rol."
                      : "Creá la primera cuenta y pasale el email + contraseña."}
                  </p>
                  {isAdmin && (
                    <Button className="bg-[#C85A32] hover:bg-[#A64A29] text-white gap-2" onClick={openNew}>
                      <Plus className="h-4 w-4" /> Nuevo usuario
                    </Button>
                  )}
                </td></tr>
              )}
              {!loading && filtered.map((u, i) => {
                const suspended = (u.account_status || "ACTIVE") === "SUSPENDED";
                return (
                <tr
                  key={u.id}
                  className={`border-b border-[#F0EBE0] hover:bg-[#FBF8F2] fade-row ${suspended ? "bg-[#FBEBEB]/40" : ""}`}
                  style={{ animationDelay: `${i * 20}ms` }}
                  data-testid={`cli-row-${u.id}`}
                >
                  <td className="px-5 py-4 font-medium text-[#1A1A1A]">{u.nombre}</td>
                  <td className="px-5 py-4">
                    <RoleBadge role={u.role} />
                  </td>
                  <td className="px-5 py-4">
                    <AccountStatusBadge user={u} />
                  </td>
                  <td className="px-5 py-4 text-sm text-[#595959]"><span className="font-mono">{u.email}</span></td>
                  <td className="px-5 py-4 text-sm font-mono text-[#595959] hidden md:table-cell">{u.telefono || "—"}</td>
                  <td className="px-5 py-4">
                    <Switch checked={!!u.activo} onCheckedChange={() => toggleActivo(u)} disabled={!isAdmin} data-testid={`cli-activo-${u.id}`} />
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex justify-end gap-1">
                      {isAdmin && u.role === "cliente" && (
                        suspended ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1.5 text-[#3F6B3C] hover:bg-[#DAE6D7]"
                            onClick={() => reactivate(u)}
                            title="Rehabilitar cuenta"
                            data-testid={`cli-reactivate-${u.id}`}
                          >
                            <ShieldCheck className="h-4 w-4" />
                            <span className="hidden lg:inline text-[11px] font-semibold">Rehabilitar</span>
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1.5 text-[#8E2E2E] hover:bg-[#FBEBEB]"
                            onClick={() => openSuspend(u)}
                            title="Suspender cuenta"
                            data-testid={`cli-suspend-${u.id}`}
                          >
                            <Ban className="h-4 w-4" />
                            <span className="hidden lg:inline text-[11px] font-semibold">Suspender</span>
                          </Button>
                        )
                      )}
                      {isAdmin && (
                        <Button variant="ghost" size="sm" className="gap-1.5 text-[#595959] hover:text-[#C85A32] hover:bg-[#F4EAE6]" onClick={() => openEdit(u)} data-testid={`cli-edit-${u.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {isAdmin && u.id !== me?.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-[#8E2E2E] hover:bg-[#FBEBEB]" data-testid={`cli-del-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Eliminar usuario</AlertDialogTitle>
                              <AlertDialogDescription>¿Eliminar la cuenta de "{u.nombre}" ({u.email})? Los pedidos existentes no se borran.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Volver</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(u)} className="bg-[#B23A3A] hover:bg-[#8E2E2E]">Eliminar</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Modificá los datos. Dejá la contraseña vacía para mantenerla."
                : "El usuario podrá ingresar a la app con el email y la contraseña que asignes."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="cli-form-nombre" />
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5" /> Rol
              </Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
                disabled={editingIsSelf}
              >
                <SelectTrigger data-testid="cli-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ALL_ROLES.map(({ value, label, Icon, tip }) => (
                    <SelectItem key={value} value={value} data-testid={`cli-form-role-${value}`}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-[#C85A32]" />
                        <span className="font-semibold">{label}</span>
                        <span className="text-[11px] text-[#8C8C8C] hidden sm:inline">— {tip}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editingIsSelf && (
                <p className="text-[11px] text-[#A6751F] mt-1">No podés cambiar tu propio rol.</p>
              )}
            </div>
            <div>
              <Label className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> Email</Label>
              <Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cli-form-email" />
              {editing && <p className="text-[11px] text-[#8C8C8C] mt-1">El email no se puede cambiar.</p>}
            </div>
            <div>
              <Label>Contraseña{editing ? " (dejá vacío para no cambiar)" : ""}</Label>
              <Input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editing ? "••••••••" : "Mínimo 8 caracteres"}
                data-testid="cli-form-password"
              />
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+54 11..." data-testid="cli-form-tel" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-[#C85A32] hover:bg-[#A64A29] text-white" onClick={save} data-testid="cli-form-save">
              {editing ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend dialog */}
      <Dialog open={!!suspendTarget} onOpenChange={(o) => !o && setSuspendTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#8E2E2E]">
              <Ban className="h-5 w-5" /> Suspender cuenta
            </DialogTitle>
            <DialogDescription>
              El cliente <b>{suspendTarget?.nombre}</b> ({suspendTarget?.email}) va a poder iniciar
              sesión y ver su historial, pero <b>no podrá crear nuevos pedidos</b> hasta que lo rehabilites.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-[12px]">Motivo (opcional)</Label>
            <Textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Ej: Cuenta corriente vencida"
              className="bg-white min-h-[80px] resize-y"
              data-testid="cli-suspend-reason"
              maxLength={300}
            />
            <p className="text-[11px] text-[#8C8C8C]">
              Este motivo queda registrado en auditoría y sirve para recordar por qué se suspendió.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendTarget(null)} disabled={suspendBusy}>
              Cancelar
            </Button>
            <Button
              onClick={confirmSuspend}
              disabled={suspendBusy}
              className="bg-[#B23A3A] hover:bg-[#8E2E2E] text-white gap-2"
              data-testid="cli-suspend-confirm"
            >
              <Ban className="h-4 w-4" />
              {suspendBusy ? "Suspendiendo..." : "Suspender cuenta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChipFilter({ active, onClick, label, Icon, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
        active
          ? "bg-[#C85A32] text-white shadow-sm"
          : "bg-white text-[#595959] border border-[#E5E0D8] hover:border-[#C85A32] hover:text-[#C85A32]"
      }`}
      data-testid={testid}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}

function RoleBadge({ role }) {
  const meta = ALL_ROLES.find((r) => r.value === role);
  if (!meta) return <span className="text-[11px] text-[#8C8C8C]">{role}</span>;
  const Icon = meta.Icon;
  const cls = ROLE_BADGE[role] || ROLE_BADGE.cliente;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.1em] border ${cls}`}
      data-testid={`cli-role-${role}`}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

function AccountStatusBadge({ user }) {
  const suspended = (user?.account_status || "ACTIVE") === "SUSPENDED";
  if (!suspended) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.1em] border bg-[#DAE6D7] text-[#3F6B3C] border-[#BCD3B7]"
        data-testid={`cli-status-${user?.id}`}
      >
        <ShieldCheck className="h-3 w-3" />
        Activa
      </span>
    );
  }
  return (
    <div className="flex flex-col items-start gap-1">
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.1em] border bg-[#FBEBEB] text-[#8E2E2E] border-[#E8B5B5]"
        data-testid={`cli-status-${user?.id}`}
        title={user?.suspension_reason || "Cuenta suspendida"}
      >
        <ShieldOff className="h-3 w-3" />
        Suspendida
      </span>
      {user?.suspension_reason && (
        <span className="text-[10px] text-[#8E2E2E] max-w-[220px] line-clamp-2 leading-tight">
          {user.suspension_reason}
        </span>
      )}
    </div>
  );
}
